import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Trophy, Crown } from "lucide-react";
import { useAutoFocus } from "@/hooks/useAutoFocus";
import {
  hasProfile,
  getNickname,
  createPlayer,
  getMe,
  getLeaderboard,
} from "@/play/leaderboard";

const ORD = { 1: "Primo", 2: "Secondo", 3: "Terzo" };

/**
 * Classifica — identità anonima (token + nickname permanente).
 * La partecipazione è facoltativa: chi non ha un nickname può crearlo qui.
 * Il record personale mostrato è quello ufficiale del server.
 */
export default function Leaderboard() {
  const navigate = useNavigate();
  const headingRef = useAutoFocus();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [nickname, setNicknameInput] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const nickRef = useRef(null);

  const profileExists = hasProfile();

  const load = async () => {
    setLoading(true);
    try {
      const board = await getLeaderboard(100);
      setRows(Array.isArray(board) ? board : []);
    } catch {
      setRows([]);
    }
    if (hasProfile()) {
      try {
        setMe(await getMe());
      } catch {
        setMe(null);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    if (creating) return;
    setError("");
    const nick = nickname.trim();
    if (nick.length < 3) {
      setError("Il nickname deve avere almeno 3 caratteri.");
      return;
    }
    setCreating(true);
    try {
      await createPlayer(nick);
      await load();
    } catch (err) {
      if (err.status === 409) {
        setError("Nome già esistente. Scegline un altro.");
      } else if (err.status === 422) {
        setError("Nickname non valido. Usa 3–20 lettere, numeri, spazio, _ o -.");
      } else {
        setError(err.message || "Errore durante la creazione.");
      }
      if (nickRef.current) nickRef.current.focus();
    } finally {
      setCreating(false);
    }
  };

  const myNick = getNickname();

  return (
    <main className="screen" data-testid="leaderboard-screen">
      <div className="screen__header">
        <button
          type="button"
          className="back-btn"
          data-testid="leaderboard-back-button"
          onClick={() => navigate("/")}
          aria-label="Torna alla schermata principale"
        >
          <ChevronLeft size={16} strokeWidth={2.5} aria-hidden="true" />
          Indietro
        </button>
        <h1 className="screen__title" tabIndex={-1} ref={headingRef}>
          Classifica
        </h1>
      </div>

      {/* Record personale (ufficiale, dal server) oppure invito a partecipare */}
      {profileExists ? (
        <div className="lb__me" data-testid="leaderboard-me">
          <div className="lb__me-info">
            <span className="lb__me-label">Il tuo record</span>
            <span className="lb__me-nick" data-testid="leaderboard-my-nickname">
              {me?.nickname || myNick}
            </span>
          </div>
          <div className="lb__me-stats">
            <span className="lb__me-score" data-testid="leaderboard-my-best">
              {me ? me.bestScore : "…"}
            </span>
            {me &&
              (me.rank ? (
                <span className="lb__me-rank" data-testid="leaderboard-my-rank">
                  #{me.rank}
                </span>
              ) : (
                <span className="lb__me-rank lb__me-rank--none">Nessun record</span>
              ))}
          </div>
        </div>
      ) : (
        <div className="lb__join" data-testid="leaderboard-join">
          <h2 className="section-label">Entra in classifica</h2>
          <p className="doc__p">
            Scegli un nickname per comparire nella classifica globale. Attenzione: il
            nickname è <strong>permanente</strong> e non potrà essere modificato.
          </p>
          <form className="lb__form" onSubmit={onCreate}>
            <input
              ref={nickRef}
              type="text"
              className="lb__input"
              data-testid="leaderboard-nickname-input"
              placeholder="Il tuo nickname"
              value={nickname}
              onChange={(e) => setNicknameInput(e.target.value)}
              maxLength={20}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              aria-label="Scegli il tuo nickname"
              aria-describedby={error ? "lb-nick-error" : undefined}
            />
            <button
              type="submit"
              className="btn-primary lb__submit"
              data-testid="leaderboard-create-button"
              disabled={creating}
            >
              {creating ? "CREO…" : "CREA"}
            </button>
          </form>
          {error && (
            <p
              className="lb__error"
              id="lb-nick-error"
              role="alert"
              data-testid="leaderboard-error"
            >
              {error}
            </p>
          )}
        </div>
      )}

      <h2 className="section-label">Migliori giocatori</h2>

      {loading ? (
        <p className="doc__p" data-testid="leaderboard-loading">
          Caricamento…
        </p>
      ) : rows.length === 0 ? (
        <p className="doc__p" data-testid="leaderboard-empty">
          Nessun punteggio ancora. Gioca una partita per essere il primo!
        </p>
      ) : (
        <>
          {/* Podio: primi 3 evidenziati (ordine visivo 2-1-3). Accessibile con VoiceOver. */}
          <div
            className="lb__podium"
            data-testid="leaderboard-podium"
            role="list"
            aria-label="Podio dei primi tre giocatori"
          >
            {[1, 0, 2].map((idx) => {
              const r = rows[idx];
              if (!r) {
                return (
                  <div
                    className="lb__podium-slot lb__podium-slot--empty"
                    key={`empty-${idx}`}
                    aria-hidden="true"
                  />
                );
              }
              const pos = idx + 1;
              const isMe = profileExists && myNick && r.nickname === myNick;
              return (
                <div
                  key={idx}
                  className={`lb__podium-slot lb__podium-slot--${pos}${
                    isMe ? " lb__podium-slot--me" : ""
                  }`}
                  role="listitem"
                  data-testid={`leaderboard-podium-${pos}`}
                  aria-label={`${ORD[pos]} posto: ${r.nickname}, ${r.bestScore} punti`}
                >
                  <span className="lb__podium-nick" aria-hidden="true">
                    {r.nickname}
                  </span>
                  <div className="lb__podium-bar" aria-hidden="true">
                    <span className="lb__podium-rank">
                      {pos === 1 ? <Crown size={18} strokeWidth={2.5} /> : pos}
                    </span>
                    <span className="lb__podium-score">{r.bestScore}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <ol className="lb__list" data-testid="leaderboard-list">
            {rows.map((r, i) => {
              const pos = i + 1;
              const isMe = profileExists && myNick && r.nickname === myNick;
              return (
                <li
                  key={`${r.nickname}-${i}`}
                  className={`lb__row${isMe ? " lb__row--me" : ""}`}
                  data-testid={`leaderboard-row-${i}`}
                  aria-label={`Posizione ${pos}: ${r.nickname}, ${r.bestScore} punti`}
                >
                  <span className="lb__pos" aria-hidden="true">
                    {i === 0 ? <Crown size={16} strokeWidth={2.5} /> : pos}
                  </span>
                  <span className="lb__nick" aria-hidden="true">
                    {r.nickname}
                  </span>
                  <span className="lb__score" aria-hidden="true">
                    {r.bestScore}
                  </span>
                </li>
              );
            })}
          </ol>
        </>
      )}

      <div className="lb__cta">
        <button
          type="button"
          className="btn-primary"
          data-testid="leaderboard-play-button"
          onClick={() => navigate("/game")}
        >
          <Trophy size={16} strokeWidth={2.5} aria-hidden="true" style={{ marginRight: 8 }} />
          GIOCA
        </button>
      </div>
    </main>
  );
}
